import { memo } from "react";

import { Heading } from "@components/Heading";
import { Text } from "@components/Text";
import { type ThemeMode, type RatingStat } from "@root/tablescape/state";
import { ReviewRatingBar } from "@sections/ProductReviews/ReviewRatingBar";
import { ReviewStars } from "@sections/ProductReviews/ReviewStars";
import { type TTailwindString, tw } from "classnames";

const styles = {
  container: tw("w-full", "pb-3"),
  containerBorder: tw(
    "border-0",
    "border-b",
    "border-solid",
    "border-char-300"
  ),
  wrapper: tw(
    "flex",
    "flex-col",
    "md:flex-row",
    "flex-nowrap",
    "md:flex-wrap",
    "mb-6"
  ),
  summary: tw("mb-6", "md:mb-0", "md:mt-2", "md:mr-30", "text-primary"),
  reviewStars: tw("mb-1", "md:mb-2", "text-special"),
  distributions: tw("md:flex-1", "md:max-w-[350px]" as TTailwindString),
  disclaimer: tw("text-primary-muted"),
};

type Props = {
  perScoreRatingStat: RatingStat[];
  reviewCount: string;
  reviewScore: string;
  showBundleDisclaimer: boolean;
  theme: ThemeMode;
};

const ReviewsSectionSummaryViewBase = ({
  perScoreRatingStat,
  reviewCount,
  reviewScore,
  showBundleDisclaimer,
  theme,
}: Props) => {
  return (
    <div
      className={tw({
        [styles.container]: true,
        [styles.containerBorder]: !showBundleDisclaimer,
      })}
    >
      <div className={styles.wrapper}>
        <div className={styles.summary}>
          <Heading as="display">{reviewScore}</Heading>
          <ReviewStars
            className={styles.reviewStars}
            rating={parseFloat(reviewScore)}
          />
          <Text as="body1">
            {reviewCount} Reviews{showBundleDisclaimer ? "*" : ""}
          </Text>
        </div>

        <div className={styles.distributions}>
          {perScoreRatingStat.map((ratingStat: RatingStat) => {
            return (
              <ReviewRatingBar
                key={`rating-${ratingStat.label}`}
                percentage={ratingStat.percentage}
                prefixText={ratingStat.label}
                suffixText={`${ratingStat.count} (${ratingStat.percentage}%)`}
                theme={theme}
              />
            );
          })}
        </div>
      </div>
      {showBundleDisclaimer && (
        <Text as="caption" className={tw(styles.disclaimer)} size="xs">
          *Based on an aggregate of product reviews for individual bundle
          components. Reviews for each individual products below.
        </Text>
      )}
    </div>
  );
};

export const ReviewsSectionSummaryView = memo(ReviewsSectionSummaryViewBase);
